import { useCallback } from "react";

import { Message } from "@/components/Messaging/types";
import { useChannel } from "@/components/Messaging/useChannel";
import { WEBSOCKET_MESSAGE_NAME } from "@/constants";
import { useUser } from "@/context/user-context";

// WebSocket message types
export enum TestRunStatus {
  QUEUED = "QUEUED",
  RUNNING = "RUNNING",
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}

export interface WorkflowTestRunEnvelope {
  message_name: string;
  run_id: string;
  workflow: {
    id: string;
    version_id: string;
    version_number: number;
  };
  timestamp: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
}

export interface WorkflowTestRunKickedOffPayload {
  requested_by_user_id: string;
  workspace_id: string;
  status: "QUEUED";
  scenario_name?: string;
  input_variables_meta: {
    keys: string[];
    size_bytes_estimate: number;
  };
}

export interface WorkflowTestRunUpdatePayload {
  status: "QUEUED" | "RUNNING";
  trace_id?: string; // Required when status becomes "RUNNING"
  progress?: { percentage: number };
  info?: string;
}

export interface WorkflowTestRunSucceededPayload {
  duration_ms: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  final_output: any;
  summary?: {
    total_nodes?: number;
    slowest_node?: { id: string; duration_ms: number };
    token_usage?: {
      prompt: number;
      completion: number;
      total: number;
      cost_usd?: number;
    };
  };
  trace_id?: string;
}

export interface WorkflowTestRunFailedPayload {
  duration_ms?: number;
  error: {
    code: string;
    message: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details?: Record<string, any>;
  };
  trace_id?: string;
}

export type TestRunMessage =
  | { type: "kicked_off"; data: WorkflowTestRunKickedOffPayload }
  | { type: "update"; data: WorkflowTestRunUpdatePayload }
  | { type: "succeeded"; data: WorkflowTestRunSucceededPayload }
  | { type: "failed"; data: WorkflowTestRunFailedPayload };

interface UseWorkflowTestRunWebSocketProps {
  runId: string | null;
  onMessage: (message: TestRunMessage) => void;
}

export const useWorkflowTestRunWebSocket = ({
  runId,
  onMessage,
}: UseWorkflowTestRunWebSocketProps) => {
  const { user } = useUser();

  // Memoize message handlers to avoid stale closures
  const handleKickedOff = useCallback(
    (message: Message) => {
      try {
        const envelope: WorkflowTestRunEnvelope = JSON.parse(message.data);

        // Filter by run_id if specified
        if (runId && envelope.run_id !== runId) {
          return;
        }

        onMessage({ type: "kicked_off", data: envelope.payload });
      } catch {
        // eslint-disable-next-line no-console
        console.error("Error handling kicked off");
      }
    },
    [runId, onMessage],
  );

  // Listen for kicked off messages
  useChannel(
    `user:${user.id}`,
    WEBSOCKET_MESSAGE_NAME.WORKFLOW_TEST_RUN_KICKED_OFF,
    handleKickedOff,
  );

  const handleUpdate = useCallback(
    (message: Message) => {
      try {
        const envelope: WorkflowTestRunEnvelope = JSON.parse(message.data);

        // Filter by run_id if specified
        if (runId && envelope.run_id !== runId) {
          return;
        }

        onMessage({ type: "update", data: envelope.payload });
      } catch {
        // eslint-disable-next-line no-console
        console.error("Error handling update");
      }
    },
    [runId, onMessage],
  );

  // Listen for update messages
  useChannel(`user:${user.id}`, WEBSOCKET_MESSAGE_NAME.WORKFLOW_TEST_RUN_UPDATE, handleUpdate);

  const handleSucceeded = useCallback(
    (message: Message) => {
      try {
        const envelope: WorkflowTestRunEnvelope = JSON.parse(message.data);

        // Filter by run_id if specified
        if (runId && envelope.run_id !== runId) {
          return;
        }

        onMessage({ type: "succeeded", data: envelope.payload });
      } catch {
        // eslint-disable-next-line no-console
        console.error("Error handling succeeded");
      }
    },
    [runId, onMessage],
  );

  // Listen for succeeded messages
  useChannel(
    `user:${user.id}`,
    WEBSOCKET_MESSAGE_NAME.WORKFLOW_TEST_RUN_SUCCEEDED,
    handleSucceeded,
  );

  const handleFailed = useCallback(
    (message: Message) => {
      try {
        const envelope: WorkflowTestRunEnvelope = JSON.parse(message.data);

        // Filter by run_id if specified
        if (runId && envelope.run_id !== runId) {
          return;
        }

        onMessage({ type: "failed", data: envelope.payload });
      } catch {
        // eslint-disable-next-line no-console
        console.error("Error handling failed");
      }
    },
    [runId, onMessage],
  );

  // Listen for failed messages
  useChannel(`user:${user.id}`, WEBSOCKET_MESSAGE_NAME.WORKFLOW_TEST_RUN_FAILED, handleFailed);
};
